"use client"

import { useCallback, useState, useEffect } from "react"

import { Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { debounce } from "lodash"
import { useSearchParams, usePathname, useRouter } from "next/navigation"
import { useTranslations } from "next-intl"

export type SearchBarProps = {
  onChange: (value: string) => void
}

/**
 * A search bar component that allows users to search for products.
 */
export function SearchBar() {
  const searchParams = useSearchParams()
  const pathname = usePathname()
  const router = useRouter()
  const [value, setValue] = useState(searchParams.get("q") ?? "")
  const [isSearching, setIsSearching] = useState(false)
  const t = useTranslations("Store.Products")
  // Create a memoized debounced function that updates the URL and calls onChange
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((searchTerm: string) => {
      const params = new URLSearchParams(searchParams.toString())

      if (searchTerm) {
        params.set("q", searchTerm)
      } else {
        params.delete("q")
      }

      // Reset to first page when searching
      params.delete("page")

      // Update URL without navigation
      router.replace(`${pathname}?${params.toString()}`, { scroll: false })

      // Call the onChange handler for client-side data fetching
      setIsSearching(false)
    }, 500),
    [searchParams, pathname, router]
  )

  // Clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel()
    }
  }, [debouncedSearch])

  const handleSearch = useCallback(
    (term: string) => {
      setValue(term)
      setIsSearching(true)
      debouncedSearch(term)
    },
    [debouncedSearch]
  )

  return (
    <div className="relative w-full md:w-80">
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
      <Input
        type="search"
        placeholder={t("Search.placeholder")}
        className="w-full pl-9"
        value={value}
        onChange={(e) => handleSearch(e.target.value)}
      />
      {isSearching && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-500 border-t-transparent dark:border-gray-400"></div>
        </div>
      )}
    </div>
  )
}

export function SearchResults() {
  return (
    <div>
      <h1>Search Results</h1>
    </div>
  )
}